import React, { FormEvent, useEffect, useState } from 'react'
import { Form } from 'react-router-dom';
import { Alert, Button, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import { IAddress } from '../../types/base';

import { useListContext } from '../../contexts/listContext';

import { CustomerService } from '../../services/customerService';
import { AppConfig } from '../../config/appConfig';

export interface ICustomerAddressesForm {
	id: string;
	shippingAddress: IAddress;
	mailingAddress: IAddress;
	reloadData: () => void;
}

const CustomerAddressesForm: React.FunctionComponent<ICustomerAddressesForm> = ({ id, shippingAddress, mailingAddress, reloadData }) => {
	const listContext = useListContext();
	const [shippingAddressFields, setShippingAddressFields] = useState(shippingAddress);
	const [mailingAddressFields, setMailingAddressFields] = useState(shippingAddress);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [successMessage, setSuccessMessage] = useState<string | undefined>();

	useEffect(() => {
		setShippingAddressFields(shippingAddress);
	}, [shippingAddress])

	useEffect(() => {
		setMailingAddressFields(mailingAddress);
	}, [mailingAddress])

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		clearSaveResult();
		setIsSaving(true);

		CustomerService.setAddresses(id, shippingAddressFields, mailingAddressFields)
			.then((response) => {
				console.log('CustomerService.setAddresses: SUCCESS', response);
				setSuccessMessage("Customer addresses have been saved.");
				setTimeout(() => {
					setSuccessMessage(undefined);
				}, AppConfig.editSuccessToastMs);
				reloadData();
			})
			.catch((error) => {
				console.log('CustomerService.setAddresses: ERROR', error);
				setErrorMessage("We encountered an error while processing your request. If the problem persists, please contact your administrator.");
			})
			.finally(() => {
				setIsSaving(false);
				window.scrollTo(0, 0);
			});
	}

	const clearSaveResult = () => {
		setErrorMessage(undefined);
		setSuccessMessage(undefined);
	};

	return (
		<section className="hwi-addresses-form">
			{successMessage &&
				<Alert className="alert-success"
					severity="success"
					action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
					{successMessage}
				</Alert>
			}
			{errorMessage &&
				<Alert className="alert-error" 
					severity="error" 
					action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
						{errorMessage}
				</Alert>		
			}
			<Form onSubmit={(event) => handleSubmit(event)}>
				<div className="fields">
					<div className="panel">
						<div className="title">Shipping Address</div>
						<div className="content">
							<div className="row">
								<InputLabel>Street Address *</InputLabel>
								<TextField className="street-field" name="street" required value={shippingAddressFields.street} size="small" onChange={(event) => { setShippingAddressFields({ ...shippingAddressFields, ["street"]: event.target.value }) }}></TextField>
							</div>
							<div className="row">
								<InputLabel>Street Address 2</InputLabel>
								<TextField className="street-field" name="street2" value={shippingAddressFields.street2} size="small" onChange={(event) => { setShippingAddressFields({ ...shippingAddressFields, ["street2"]: event.target.value }) }}></TextField>
							</div>
							<div className="row">
								<InputLabel>City *</InputLabel>
								<TextField className="city-field" name="city" required value={shippingAddressFields.city} size="small" onChange={(event) => { setShippingAddressFields({ ...shippingAddressFields, ["city"]: event.target.value }) }}></TextField>
							</div>
							<div className="row">
								<InputLabel>State *</InputLabel>
								<Select
									size="small"
									value={shippingAddressFields.state}
									required
									onChange={(event) => { setShippingAddressFields({ ...shippingAddressFields, ["state"]: event.target.value }) }}
								>
									{listContext.states.map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
							</div>
							<div className="row">
								<InputLabel>Zip *</InputLabel>
								<TextField className="zip-field" name="zip" required value={shippingAddressFields.zip} size="small" onChange={(event) => { setShippingAddressFields({ ...shippingAddressFields, ["zip"]: event.target.value }) }}></TextField>
							</div>
						</div>
					</div>
					<div className="panel">
						<div className="title">Mailing Address</div>
						<div className="content">
							<div className="row">
								<InputLabel>Street Address</InputLabel>
								<TextField className="street-field" name="street" value={mailingAddressFields.street} size="small" onChange={(event) => { setMailingAddressFields({ ...mailingAddressFields, ["street"]: event.target.value }) }}></TextField>
							</div>
							<div className="row">
								<InputLabel>Street Address 2</InputLabel>
								<TextField className="street-field" name="street2" value={mailingAddressFields.street2} size="small" onChange={(event) => { setMailingAddressFields({ ...mailingAddressFields, ["street2"]: event.target.value }) }}></TextField>
							</div>
							<div className="row">
								<InputLabel>City</InputLabel>
								<TextField className="city-field" name="city" value={mailingAddressFields.city} size="small" onChange={(event) => { setMailingAddressFields({ ...mailingAddressFields, ["city"]: event.target.value }) }}></TextField>
							</div>
							<div className="row">
								<InputLabel>State</InputLabel>
								<Select
									size="small"
									value={mailingAddressFields.state}
									onChange={(event) => { setMailingAddressFields({ ...mailingAddressFields, ["state"]: event.target.value }) }}
								>
									{listContext.states.map((item, index) => (
										<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
									))}
								</Select>
							</div>
							<div className="row">
								<InputLabel>Zip</InputLabel>
								<TextField className="zip-field" name="name" value={mailingAddressFields.zip} size="small" onChange={(event) => { setMailingAddressFields({ ...mailingAddressFields, ["zip"]: event.target.value }) }}></TextField>
							</div>
						</div>
					</div>
				</div>

				<div className="controls">
					<Button type="submit" className="save-button" disabled={isSaving} variant="contained">{isSaving ? "Saving..." : "Save Addresses"}</Button>
				</div>
			</Form>
		</section>
	)
}

export default CustomerAddressesForm;